import angular from 'angular';

import roles from 'app/validators/roles';
import { initGrid } from 'utils/grid/grid';
import { bulkUpdate } from 'user/resources/users.api';

import { checkAccess } from 'app/actions/app.actions';
import { downloadUsersReport, fetchUsers } from 'user/actions/users.actions';
import { fetchUnreadMessagesCount } from 'message/actions/unread-count.actions';
import { fetchUserClassDictionary } from 'app/actions/dictionaries/user-class-dictionary.actions';
import { fetchUserTemplatesDictionary } from 'app/actions/dictionaries/user-templates-dictionary.actions';
import { fetchOutletTypesDictionary } from 'app/actions/dictionaries/outlet-types-dictionary.actions';
import template from './manage-users.html';

const DOWNLOAD_OPTIONS = [
  {
    label: 'CSV',
    extension: 'csv',
  },
];

const mapStateToThis = ({ session, users, config, dictionary }) => {
  const { locale, isAdmin, retailer } = session;
  const { userClass, userTemplates, outletTypes } = dictionary;
  const { items, pagination, isLoading } = users;
  const { sortColumnKey, directionKey, pageSizeKey, defaultPageSizes } = config;
  const terms = dictionary.terms[locale];

  return {
    locale,
    session,
    pagination,
    isLoading,
    isAdmin,
    isChainHead: retailer.isChainHead,
    isSuperChain: retailer.isSuperChain,
    templates: userTemplates,
    userClass,
    users: items,
    sortColumnKey,
    directionKey,
    pageSizeKey,
    defaultPageSizes,
    outletTypes,
    terms,
  };
};

const mapDispatchToThis = {
  fetchUsers,
  downloadUsersReport,
  checkAccess,
  fetchUnreadMessagesCount,
  fetchUserClassDictionary,
  fetchUserTemplatesDictionary,
  fetchOutletTypesDictionary,
};

@Inject('$ngRedux', '$state', 'gettextCatalog', 'Notifications')
class ManageUsersController {
  constructor() {
    this.unsubscribe = this.$ngRedux.connect(mapStateToThis, mapDispatchToThis)(this);
  }

  $onInit() {
    this.bulkUpdate = bulkUpdate.bind(this);
    this.canBulkUpdateRole = this.checkAccess('user.bulkUpdateRole');
    this.downloadOptions = DOWNLOAD_OPTIONS;
    this.canSeeRetailerNo = this.isAdmin || this.isSuperChain || this.isChainHead;

    this.actions = this.getActions();
    this.initSelectionParameters();
    this.initTable();
    this.fetchUnreadMessagesCount();
    this.fetchOutletTypesDictionary();
  }

  $onDestroy() {
    this.unsubscribe();
  }

  async initTable() {
    this.headers = this.getHeaders();
    this.defaultParams = {
      [this.sortColumnKey]: 'contact.lastName',
      [this.directionKey]: true,
      [this.pageSizeKey]: this.defaultPageSizes[2],
    };
    this.gridModel = initGrid(this.fetchUsers, this.defaultParams);

    const viewType = this.isAdmin ? 'all' : ['RETAILER_SECONDARY', 'RETAILER_PRIMARY'];
    Promise.all([this.fetchUserTemplatesDictionary(viewType), this.fetchUserClassDictionary()]).then(() => {
      this.search();
    });
  }

  initSelectionParameters() {
    this.selected = [];
    this.excluded = [];
    this.selectedAll = false;
    this.selectedTemplate = false;
    this.selectedUsersCount = 0;
    this.selectedUserClass = false;
    this.isUserClassFilterSelected = false;
    this.indeterminateChecbox = false;
    this.preservedFilters = {};
  }

  clearFilter() {
    this.gridModel = initGrid(this.fetchUsers, this.defaultParams);
    this.initSelectionParameters();
    return this.search();
  }

  async search() {
    this.initSelectionParameters();

    await this.gridModel.applyFilters();
    this.isUserClassFilterSelected = this.gridModel.pagingModel.filters.userClass;
    this.selectedUserClass = this.isUserClassFilterSelected;
    this.preservedFilters = { ...this.gridModel.pagingModel.filters };
    this.preservedPagingModel = { ...this.gridModel.pagingModel, filters: this.preservedFilters };

    if (this.isUserClassFilterSelected && this.selectedAll) {
      this.selected = this.users.map((user) => user.id);
    }
  }

  getHeaders() {
    let headers = [
      {
        text: this.gettextCatalog.getString('Name'),
        field: 'contact.lastName',
        sortable: true,
      },
      {
        text: this.gettextCatalog.getString('Retailer No.'),
        field: 'retailers.retailerId',
        sortable: true,
      },
      {
        text: this.gettextCatalog.getString('Retailer Type'),
        field: 'outletType',
        sortable: true,
      },
      {
        text: this.gettextCatalog.getString('Email'),
        field: 'username',
        sortable: true,
      },
      {
        text: this.gettextCatalog.getString('Mobile Phone'),
        field: 'contact.mobilePhone',
        sortable: true,
      },
      {
        text: this.gettextCatalog.getString('User Class'),
        field: 'userClass',
        sortable: true,
      },
      {
        text: this.gettextCatalog.getString('Role'),
        field: 'templateId',
        sortable: false,
      },
      {
        text: this.gettextCatalog.getString('System Access'),
        field: 'status.statusCode',
        sortable: true,
        textAlignment: 'text-right',
      },
    ];

    if (!this.canSeeRetailerNo) {
      headers = headers.filter((header) => header.field !== 'retailers.retailerId');
    }

    return headers;
  }

  onDownloadReport(extension) {
    return this.downloadUsersReport({
      extension,
      ...this.gridModel.pagingModel,
      pageSize: 0, // Forces BE to export the entire data set
    });
  }

  toggleSelectAll() {
    this.selectedAll = !this.selectedAll;

    if (!this.selectedAll) {
      this.selected = [];
      this.excluded = [];
    }

    if (this.selectedAll) {
      this.selected = this.users.map((user) => user.id).filter((id) => !this.excluded.includes(id));
    }

    this.calculateSelectedUsersCount();
    this.setIndeterminateChecboxStatus();
  }

  displayNotification() {
    this.Notifications.alertError(this.gettextCatalog.getString('To select all first use the User Class filter.'));
  }

  deselectAll() {
    this.selectedAll = false;
    this.selected = [];
    this.excluded = [];
    this.selectedUserClass = false;
    this.selectedTemplate = false;
    this.indeterminateChecbox = false;
  }

  select(userData) {
    const isAlreadySelected = this.selected.find((id) => id === userData.id);
    const hasPagination = this.pagination.pagesCount > 1;

    if (!isAlreadySelected) {
      this.selected = [...this.selected, ...[userData.id]];
      this.calculateExcludedUserIds(userData.id);
    }

    if (isAlreadySelected) {
      const selectedIds = this.selected.filter((id) => id !== userData.id);
      this.selected = [...selectedIds];
      this.calculateExcludedUserIds(userData.id);
    }

    if (!hasPagination && this.selected.length === this.users.length) {
      this.selectedAll = true;
      this.excluded = [];
    }

    if (!hasPagination && this.selected.length === 0) {
      this.selectedAll = false;
      this.excluded = [];
    }

    this.setUserClassAccordingToSelectedUserEntity(userData.userClass);
    this.calculateSelectedUsersCount();
    this.setIndeterminateChecboxStatus();
  }

  setUserClassAccordingToSelectedUserEntity(userClass) {
    if (this.isUserClassFilterSelected) return;

    this.selectedUserClass = this.selected.length ? userClass : false;
  }

  calculateExcludedUserIds(id) {
    if (!this.selectedAll) {
      this.excluded = [];
      return;
    }

    const isAlreadyExcluded = this.excluded.find((excludedId) => excludedId === id);

    if (!isAlreadyExcluded) {
      this.excluded = [...this.excluded, ...[id]];
    }

    if (isAlreadyExcluded) {
      const excludedIds = this.excluded.filter((excludedId) => excludedId !== id);
      this.excluded = [...excludedIds];
    }
  }

  isDisabled(userClass) {
    const isPrimaryUserType = roles.PRIMARY_USER_ROLES.includes(userClass);
    const hasDifferentUserClassTheSelected = this.selectedUserClass && this.selectedUserClass !== userClass;

    return isPrimaryUserType || hasDifferentUserClassTheSelected;
  }

  isChecked(userData) {
    if (this.selectedAll) {
      return !this.excluded.includes(userData.id);
    }

    return this.selected.find((id) => id === userData.id);
  }

  async onChangeRoles() {
    const templateId = this.selectedTemplate.id;
    const searchParams = this.preservedFilters || {};
    const selectedUsers = this.selectedAll ? [] : this.selected;
    const excludedUsers = this.selectedAll ? this.excluded : [];
    const filters = {
      ...this.preservedFilters,
    };

    const { updatedCount } = await this.bulkUpdate({
      templateId,
      searchParams,
      selectedUsers,
      excludedUsers,
    });

    if (updatedCount > 0) {
      this.gridModel = initGrid(this.fetchUsers, { ...this.defaultParams, filters });
      this.search();
    }
  }

  isPrimaryUserRole(userClass) {
    return roles.PRIMARY_USER_ROLES.includes(userClass);
  }

  calculateSelectedUsersCount() {
    if (this.selectedAll) {
      this.selectedUsersCount = this.pagination.count - this.excluded.length;
      return;
    }

    this.selectedUsersCount = this.selected.length;
  }

  setIndeterminateChecboxStatus() {
    const isExcludedUserSelected = !!this.excluded.length;
    const isSomeoneSelected = !!this.selected.length;
    const isSelectedLessThanMaxUserCount = this.selectedUsersCount < this.pagination.count;

    this.indeterminateChecbox = (isSomeoneSelected && isSelectedLessThanMaxUserCount) || isExcludedUserSelected;
  }

  goToCreateAccount() {
    this.$state.go('user-createAccount');
  }

  goToCreateSecondaryUserAccount() {
    this.$state.go('user-createSecondaryUserAccount');
  }

  getActions() {
    return [
      {
        type: 'link',
        function: this.goToCreateAccount.bind(this),
        label: this.gettextCatalog.getString('Create User'),
        isAvailable: this.canCreateUser,
      },
      {
        type: 'link',
        function: this.goToCreateSecondaryUserAccount.bind(this),
        label: this.gettextCatalog.getString('Create User'),
        isAvailable: this.canCreateSecondary,
      },
      {
        type: 'download',
        function: this.onDownloadReport.bind(this),
        extension: 'csv',
        isAvailable: this.isAdmin,
      },
    ].filter(({ isAvailable }) => isAvailable === true);
  }
}

class ManageUsersComponent {
  constructor() {
    this.template = template;
    this.controller = ManageUsersController;
    this.bindings = {
      model: '<',
      searchUser: '<',
      canCreateUser: '<',
      canCreateSecondary: '<',
      api: '<',
    };
  }
}

export default angular.module('manage-users', []).component('manageUsers', new ManageUsersComponent());



// WEBPACK FOOTER //
// ./src/modules/user/components/manage-users/manage-users-component.js